import { userStore } from '@/store/user-store';
import useConverter from '@/composables/general/useConverter';

// Checks permission regarding topic (e.g. creating thought, etc.)
export default function () {
  const { toBool } = useConverter();

  /**
   * Whether topic allows thought creation based on user and config
   * @param topic topic object
   * @returns true|false
   */
  function canCreateThought(topic: any) {
    if (topic && toBool(topic.new_thoughts)) {
      if (userStore.isVirtualUser() && toBool(topic.anon_thoughts)) {
        return true;
      }
      return userStore.isLoggedIn();
    }
    return false;
  }

  /**
   * checks if thought creation requires user to be logged in first
   * NOTE: recommended to call this after checking canCreateThought(topic) first
   *
   * @param topic topic object
   * @returns true/false
   */
  function canCreateThoughtAfterLogin(topic: any) {
    return toBool(topic.new_thoughts) && !userStore.isLoggedIn();
  }

  /**
   *  Whether user is allowed to comment on the topic or not
   *
   * @param topic topic
   * @returns true|false
   */
  function canCommentOnThoughts(topic: any) {
    if (topic && toBool(topic.new_comments)) {
      if (userStore.isVirtualUser() && toBool(topic.anon_comments)) {
        return true;
      }
      return userStore.isLoggedIn();
    }
    return false;
  }
  /**
   * Whether user is allowed to edit the comment
   * @param comment comment object
   * @returns true|false
   */
  function canEditComment(comment: any) {
    if (!comment.user) return false;
    if (!userStore.getIsInitialized()) return false;
    const user = userStore.getState().user;
    if (user) {
      return user.id == comment.user.id;
    }
    return false;
  }
  /**
   *  whether commenting requires user login or not
   *  NOTE: recommended to call this after checking canCommentOnThoughts(topic) first
   *
   * @param topic topic
   * @returns true|false
   */
  function canCommentAfterLogin(topic: any) {
    return topic && toBool(topic.new_comments) && !userStore.isLoggedIn();
  }

  /**
   * Whether user can vote for the topic
   * @param topic ITopic
   * @returns true|false
   */
  function canVote(topic: any) {
    if (toBool(topic.new_votes)) {
      if (userStore.isVirtualUser() && toBool(topic.anon_votes)) {
        return true;
      }
      return userStore.isLoggedIn();
    }
    return false;
  }

  /**
   *  whether voting requires user login or not
   *  NOTE: recommended to call this after checking canVote(topic) first
   *
   * @param topic topic
   * @returns true|false
   */
  function canVoteAfterLogin(topic: any) {
    return toBool(topic.new_votes) && !userStore.isLoggedIn();
  }

  /**
   *  Whether user is allowed to add feedback_response on the topic or not
   *
   * @param topic topic
   * @returns true|false
   */
  function canFeedbackOnThoughts(topic: any) {
    if (!topic) {
      return false;
    }

    if (topic.feedback ? toBool(topic.feedback.active) : false) {
      if (userStore.isVirtualUser() && toBool(topic.feedback.anon_feedback)) {
        return true;
      }
      return userStore.isLoggedIn();
    }
    return false;
  }
  /**
   *  whether feedback_respone requires user login or not
   *  NOTE: recommended to call this after checking canFeedbackOnThoughts(topic) first
   *
   * @param topic topic
   * @returns true|false
   */
  function canFeedbackAfterLogin(topic: any) {
    if (!topic) {
      return false;
    }
    return topic.feedback ? toBool(topic.feedback.active) && !userStore.isLoggedIn() : false;
  }

  return {
    canCreateThought,
    canCommentOnThoughts,
    canCreateThoughtAfterLogin,
    canCommentAfterLogin,
    canEditComment,
    canVote,
    canVoteAfterLogin,
    canFeedbackOnThoughts,
    canFeedbackAfterLogin,
  };
}
